/* Getting the words out of whatever somebody uploaded.
 *
 * The monthly report is written from vendor reports, meeting minutes and
 * correspondence, and those arrive as Word files, PowerPoints, PDFs and the
 * occasional spreadsheet. None of that is register data and none of it will
 * ever be typed in by hand, so the first job is turning a file into plain
 * text that can be read.
 *
 * Everything except PDF is unzipped and scraped here, in the browser, and
 * only the text travels. That keeps the file itself off the network, keeps
 * the request small, and means a confidential document can be read without
 * ever being uploaded to storage.
 *
 * PDF is the exception. A PDF is not a zip of words, it is a page layout,
 * and pulling text out of one properly needs a real renderer. So a PDF goes
 * across as it is and Claude reads the pages directly, which it does well.
 *
 * Nothing here parses XML. Office files are scraped with expressions over
 * the raw markup, the same approach the PowerPoint writer takes, because a
 * parse invites a serialiser and a serialiser is where these files break.
 */

import { unzipSync, strFromU8 } from "fflate";

/* What arrives, and how it has to be handled. Anything not on this list is
   still worth recording as a document, it just cannot be read. */
export const READABLE = [
  ".txt", ".md", ".csv", ".docx", ".pptx", ".xlsx", ".xlsm", ".pdf",
];

export function extensionOf(name = "") {
  const m = /\.[A-Za-z0-9]+$/.exec(String(name).trim());
  return m ? m[0].toLowerCase() : "";
}

export const canRead = (name = "") => READABLE.includes(extensionOf(name));

/* Word's own entities, plus the numeric ones that turn up in anything that
   has been through a copy and paste out of Outlook. */
export function decode(s = "") {
  return String(s)
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(Number(d)))
    .replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}

/* Blank lines collapse, runs of spaces collapse, and the leading and
   trailing nothing goes. Office markup produces a great deal of all three. */
export function tidy(text = "") {
  return String(text)
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t\u00a0]+/g, " ")
    .split("\n")
    .map((line) => line.trim())
    .filter((line, i, all) => line || (i > 0 && all[i - 1]))
    .join("\n")
    .trim();
}

/* Word. One paragraph per line, tabs and line breaks kept, everything else
   thrown away. Tables come out as their cell text in reading order, which is
   how a person reads a minutes table anyway. */
export function textFromDocx(xml = "") {
  const paragraphs = xml.split(/<\/w:p>/).map((chunk) =>
    [...chunk.matchAll(/<w:(t|tab|br|cr)(?:\s[^>]*)?(?:\/>|>([\s\S]*?)<\/w:\1>)/g)]
      .map((m) => (m[1] === "t" ? decode(m[2] ?? "") : m[1] === "tab" ? " " : "\n"))
      .join(""),
  );
  return tidy(paragraphs.join("\n"));
}

/* PowerPoint. One line per paragraph, one blank line between slides, and the
   slide number in front, because "slide 4 says" is how the person who wrote
   the deck talks about it. */
export function textFromSlide(xml = "") {
  const paragraphs = xml.split(/<\/a:p>/).map((chunk) =>
    [...chunk.matchAll(/<a:t>([\s\S]*?)<\/a:t>/g)].map((m) => decode(m[1])).join(""),
  );
  return tidy(paragraphs.join("\n"));
}

/* Excel, deliberately shallow.
 *
 * A workbook full of tracker rows belongs in Import, not here. What this is
 * for is the spreadsheet somebody attaches to a status email with forty rows
 * of commentary on it, so it reads a capped window of each sheet and stops.
 * Shared strings are resolved because Excel stores most text in a side table
 * and the cells only hold its index. */
const CELL_CAP = 40;
const ROW_CAP = 120;

export function textFromSheet(xml = "", shared = []) {
  const rows = [];
  for (const row of xml.split(/<\/row>/).slice(0, ROW_CAP)) {
    const cells = [];
    for (const m of row.matchAll(/<c\b([^>]*)>([\s\S]*?)<\/c>/g)) {
      if (cells.length >= CELL_CAP) break;
      const type = /\bt="([^"]+)"/.exec(m[1])?.[1];
      const inline = /<is>[\s\S]*?<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/.exec(m[2])?.[1];
      const value = /<v>([\s\S]*?)<\/v>/.exec(m[2])?.[1];
      if (inline != null) cells.push(decode(inline));
      else if (value == null) cells.push("");
      else if (type === "s") cells.push(shared[Number(value)] ?? "");
      else cells.push(decode(value));
    }
    while (cells.length && !cells[cells.length - 1]) cells.pop();
    if (cells.some((c) => c)) rows.push(cells.join("\t"));
  }
  return rows.join("\n");
}

export function sharedStrings(xml = "") {
  return [...xml.matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) =>
    [...m[1].matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)].map((t) => decode(t[1])).join(""),
  );
}

/* Slides come back from a zip in whatever order the zip felt like, and
   slide10 sorts before slide2 as text. */
const slideNumber = (path) => Number(/slide(\d+)\.xml$/.exec(path)?.[1] ?? 0);

/* The whole of an Office file, from its bytes. */
export function textFromZip(bytes, extension) {
  const files = unzipSync(bytes);
  const read = (path) => (files[path] ? strFromU8(files[path]) : "");

  if (extension === ".docx") {
    const parts = ["word/document.xml", ...Object.keys(files)
      .filter((p) => /^word\/(header|footer)\d*\.xml$/.test(p)).sort()];
    return tidy(parts.map((p) => textFromDocx(read(p))).filter(Boolean).join("\n\n"));
  }

  if (extension === ".pptx") {
    const slides = Object.keys(files)
      .filter((p) => /^ppt\/slides\/slide\d+\.xml$/.test(p))
      .sort((a, b) => slideNumber(a) - slideNumber(b));
    return tidy(slides.map((p, i) => {
      const body = textFromSlide(read(p));
      return body ? `Slide ${i + 1}\n${body}` : "";
    }).filter(Boolean).join("\n\n"));
  }

  /* Sheets are named in the workbook, and the names matter: "Open items" and
     "Closed" say something the rows do not. */
  const names = [...read("xl/workbook.xml")
    .matchAll(/<sheet\b[^>]*\bname="([^"]*)"/g)].map((m) => decode(m[1]));
  const shared = sharedStrings(read("xl/sharedStrings.xml"));
  const sheets = Object.keys(files)
    .filter((p) => /^xl\/worksheets\/sheet\d+\.xml$/.test(p))
    .sort((a, b) => Number(/(\d+)\.xml$/.exec(a)[1]) - Number(/(\d+)\.xml$/.exec(b)[1]));
  return tidy(sheets.map((p, i) => {
    const body = textFromSheet(read(p), shared);
    return body ? `Sheet: ${names[i] || i + 1}\n${body}` : "";
  }).filter(Boolean).join("\n\n"));
}

/* Base64 without blowing the stack. String.fromCharCode with a spread of a
   ten megabyte array is how that happens, so it goes across in chunks. */
export function toBase64(bytes) {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/* Twenty pages of minutes is around 60,000 characters. The cap is generous
   enough that nothing real is cut and mean enough that a spreadsheet somebody
   dropped in by mistake does not turn into a bill. */
export const TEXT_CAP = 120_000;

/* What a file turns into before it is sent to be read. Either text, or a PDF
   to be read at the far end. Never both. */
export async function extractDocument(file, name = file?.name || "") {
  const extension = extensionOf(name);
  if (!canRead(name)) {
    throw new Error("That file type cannot be read. Word, PowerPoint, PDF, Excel, CSV and plain text can.");
  }

  const bytes = new Uint8Array(await file.arrayBuffer());

  if (extension === ".pdf") {
    return { kind: "pdf", pdf: toBase64(bytes), text: "" };
  }

  const text = [".txt", ".md", ".csv"].includes(extension)
    ? tidy(strFromU8(bytes))
    : textFromZip(bytes, extension === ".xlsm" ? ".xlsx" : extension);

  if (!text) throw new Error("There were no words in that file to read.");
  return { kind: "text", pdf: null, text: text.slice(0, TEXT_CAP) };
}
